// client/src/hooks/useMe.js
// Hooks for "who's logged in right now":
//   useMe()      — react-query handle. data === null when not authed (401).
//   useLogout()  — POST /auth/logout, then reset the cache and reload.
//
// Tone: 401 is a normal state, not an error. The query catches the ApiError,
// returns null for data, and never throws — pages can render the login
// screen by checking `data === null`.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';

const ME_KEY = ['me'];

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get('/api/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    // 401 is a "logged out" state, not a failure to retry.
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return failureCount < 1;
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      // Drop server cache and hard-reload so any in-flight queries cancel
      // and the user lands on the login screen with a fresh state tree.
      qc.clear();
      window.location.assign('/');
    },
  });
}

/** Trigger Google OAuth, optionally returning to a specific path post-login. */
export function startGoogleLogin(returnTo) {
  const url = returnTo
    ? `/auth/google?returnTo=${encodeURIComponent(returnTo)}`
    : '/auth/google';
  window.location.assign(url);
}
